import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import SearchBar from '../../components/common/SearchBar/SearchBar';
import MentorAvatar from '../../components/common/MentorAvatar/MentorAvatar';
import { getBookableMentors } from '../../services/mentorBookingService';
import styles from './StudentBookSession.module.css';

const StudentBookSession = () => {
  const navigate = useNavigate();
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState('');

  const loadMentors = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setMentors(await getBookableMentors());
    } catch (err) {
      console.error('Error fetching mentors:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMentors();
  }, [loadMentors]);

  const term = search.trim().toLowerCase();
  const visibleMentors = term
    ? mentors.filter((m) => (
      `${m.first_name} ${m.last_name} ${m.expertise || ''}`.toLowerCase().includes(term)
    ))
    : mentors;

  let body;
  if (loading) {
    body = (
      <div className={styles.loading}>
        <LoadingSpinner size="large" message="Loading mentors..." />
      </div>
    );
  } else if (error) {
    body = (
      <div className={styles.errorBox} role="alert">
        <span>Failed to load mentors. Please try again.</span>
        <Button size="small" variant="outline" onClick={loadMentors}>Retry</Button>
      </div>
    );
  } else if (visibleMentors.length === 0) {
    body = (
      <Card>
        <div className={styles.empty}>
          <p>{term ? 'No mentors match your search.' : 'No mentors available yet.'}</p>
        </div>
      </Card>
    );
  } else {
    body = (
      <div className={styles.grid}>
        {visibleMentors.map((mentor) => (
          <Card key={mentor.id} hover className={styles.mentorCard}>
            <MentorAvatar mentor={mentor} size={144} />
            <h3 className={styles.name} title={`${mentor.first_name} ${mentor.last_name}`}>
              {mentor.first_name} {mentor.last_name}
            </h3>
            {mentor.expertise && <span className={styles.expertise}>{mentor.expertise}</span>}
            {mentor.bio
              ? <p className={styles.bio}>{mentor.bio}</p>
              : <p className={`${styles.bio} ${styles.noBio}`}>No bio provided.</p>}
            <span className={styles.durationChip}>{mentor.session_duration_minutes} min session</span>
            <div className={styles.footer}>
              <Button
                size="small"
                onClick={() => navigate(`/student/book-session/${mentor.id}`)}
              >
                Select →
              </Button>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Choose a Mentor</h1>

      <div className={styles.search}>
        <SearchBar value={search} onChange={setSearch} placeholder="Search mentors..." />
      </div>

      {body}
    </div>
  );
};

export default StudentBookSession;
